<template>
  <v-container
    v-if="showData"
    :class="{'fill-height': !orderFound}"
    fluid
  >
    <v-row
      v-if="!orderFound"
      class="fill-height align-center justify-center text-center"
    >
      <v-col
        cols="12"
        md="8"
        sm="10"
      >
        <div class="text-center brand-green-dark--text">
          <div class="mb-6">
            <v-icon
              color="brand-primary"
              size="256"
            >
              mdi-basket-plus
            </v-icon>
          </div>

          <h1 class="brand-text-heading">
            {{ $t('Checkout.Success.NotFound.Title') }}
          </h1>

          <h4 class="brand-text-small-title mt-2">
            {{ $t('Checkout.Success.NotFound.Subtitle') }}
          </h4>
        </div>
      </v-col>

      <v-col
        cols="12"
        md="8"
        sm="10"
      >
        <brand-slider
          :stores="AppStores"
          :title="$t('Checkout.Success.BrandSlider.Title')"
          class="mx-1 mt-8 d-inline-flex"
          dense
          title-class="brand-text-heading brand-green-dark--text"
        />
      </v-col>
    </v-row>

    <template v-else>
      <v-row>
        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <div class="d-flex align-center justify-center my-6">
            <h1 class="d-inline-flex brand-green-dark--text brand-text-heading">
              {{ toLocaleUpperCase($t('Checkout.Success.Status.Title')) }}
            </h1>

            <v-chip
              class="brand-primary brand-green-dark--text justify-center font-weight-bold d-inline-flex my-auto pa-3 ma-2"
              label
              style="min-width: 80px; height:auto; min-height: 0;"
            >
              <span class="brand-text-heading">{{ orderCodeString }}</span>
            </v-chip>
          </div>

          <div
            v-if="!allOrdersCompleted"
            v-safe-html="$t('Checkout.Success.Status.Body')"
            class="text-center brand-green-dark--text text-body mt-6"
          />

          <div
            v-if="isScheduled && scheduled"
            class="mt-12 text-center"
          >
            <div class="d-inline-flex">
              <v-alert
                border="left"
                class="brand-beige-light"
                color="brand-primary"
                colored-border
                dense
                max-width="500"
              >
                <strong>{{ orderType === OrderTypeEnum.COLLECTION ? $t('Checkout.Success.Scheduled.Collection.Title') : $t('Checkout.Success.Scheduled.Delivery.Title') }}:</strong> {{ scheduled }}
              </v-alert>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <template v-for="(order, idx) in orders">
            <div
              :key="`store-list-item-${idx}`"
              class="d-flex justify-space-around mb-3"
            >
              <v-card
                class="pa-1 pa-sm-3 transparent"
                flat
                style="width: 100%; max-width: 500px"
              >
                <v-card-title class="px-0">
                  <div class="mr-3 text-center">
                    <v-avatar
                      size="50"
                      style="border: var(--v-brand-green-dark-base) solid 1px;"
                    >
                      <img :src="order.StoreLogo">
                    </v-avatar>
                  </div>

                  <div class="brand-text-heading brand-green-dark--text">
                    {{ order.StoreName }}
                  </div>
                </v-card-title>

                <v-divider class="brand-green-dark" />

                <v-card-actions>
                  <div class="fill-width body-text brand-green-dark--text">
                    <div
                      v-if="order.StatusId !== OrderStatusEnum.Completed && order.StatusId !== OrderStatusEnum.Canceled"
                      class="d-flex align-center"
                    >
                      <span class="brand-text-small">{{ toLocaleUpperCase($t('Checkout.Success.Stores.Status.Time')) }}</span>

                      <v-spacer />

                      <v-chip
                        class="px-2 justify-center brand-text-title brand-green-dark--text"
                        color="brand-beige-light"
                        label
                        small
                      >
                        <v-icon
                          class="mr-1 brand-green-dark--text"
                          small
                        >
                          mdi-clock
                        </v-icon>

                        <template v-if="isScheduled && scheduled">
                          {{ scheduled }}
                        </template>
                        <template v-else>
                          {{ order.Time }}"
                        </template>
                      </v-chip>
                    </div>


                    <div class="d-flex align-center">
                      <span class="brand-text-small">{{ toLocaleUpperCase($t('Checkout.Success.Stores.Status.Title')) }}</span>

                      <v-spacer />

                      <v-chip
                        :color="getOrderStatusColorById(order.StatusId)"
                        class="px-2 mt-2 brand-text-title -brand-beige-light--text"
                        label
                        small
                      >
                        {{ getOrderStatusNameById(order.StatusId) }}
                      </v-chip>
                    </div>
                  </div>
                </v-card-actions>
              </v-card>
            </div>
          </template>
        </v-col>
      </v-row>

      <v-row v-if="rating.loaded">
        <v-col
          cols="12"
          md="8"
          offset-md="2"
          offset-sm="1"
          sm="10"
        >
          <v-container
            class="pa-0"
            fluid
          >
            <v-row>
              <v-col cols="12">
                <v-img
                  :alt="$t('Common.App.Title')"
                  :contain="$vuetify.breakpoint.smAndUp"
                  max-height="280"
                  min-height="200"
                  src="/img/home/home-stoa.webp"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-img
                  :alt="$t('Common.App.Title')"
                  contain
                  max-height="22"
                  min-height="18"
                  src="/img/logo/logo-light.svg"
                />
              </v-col>

              <v-col
                class="text-center"
                cols="12"
              >
                <h2 class="brand-text-heading">
                  {{ toLocaleUpperCase($t('Rate.Title')) }}
                </h2>

                <h3 class="brand-text-small">
                  {{ $t('Rate.Subtitle') }}
                </h3>
              </v-col>

              <v-col
                v-if="!rating.appRated"
                class="text-center"
                cols="12"
              >
                <v-rating
                  v-model="rating.userRating"
                  background-color="grey lighten-1"
                  class="mb-4"
                  color="yellow darken-3"
                  dense
                  hover
                  size="36"
                />

                <v-slide-y-transition>
                  <div class="d-flex justify-space-around">
                    <v-textarea
                      v-show="rating.userRating > 0 && rating.userRating <= 3"
                      v-model="rating.userComment"
                      :label="$t('Rate.Comments')"
                      auto-grow
                      clearable
                      color="brand-primary"
                      filled
                      rows="3"
                      style="width: 100%; max-width: 500px;"
                    />
                  </div>
                </v-slide-y-transition>

                <v-btn
                  :disabled="rating.userRating <= 0"
                  class="brand-green-dark--text brand-text-title"
                  color="brand-primary"
                  depressed
                  large
                  @click="rateTheApp"
                >
                  {{ toLocaleUpperCase($t('Rate.Button')) }}
                </v-btn>
              </v-col>

              <v-col
                v-else
                class="text-center"
                cols="12"
              >
                <v-card
                  class="d-inline-flex"
                  color="brand-card-bg"
                  flat
                  width="150"
                >
                  <v-card-title
                    class="text-center justify-center pa-2"
                    style="font-size: 75px; line-height: 65px;"
                  >
                    <div style="width: 100%">
                      {{ rating.appRating }}
                    </div>

                    <v-rating
                      :value="parseFloat(rating.appRating)"
                      background-color="grey lighten-1"
                      class="d-inline-flex"
                      color="yellow darken-3"
                      dense
                      readonly
                      size="18"
                    />

                    <div class="d-inline-flex caption">
                      {{ rating.appReviews }} {{ $t('Rate.Reviews') }}
                    </div>
                  </v-card-title>
                </v-card>

                <div class="mt-4 brand-text-small brand-green-dark--text">
                  {{ $t('Rate.Success') }}
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import CheckoutSuccessMixin from '@/theme/default/views/checkout/CheckoutSuccess.js'

export default {
  name  : 'CheckoutSuccess',
  mixins: [CheckoutSuccessMixin]
}
</script>

<style scoped src="@/theme/default/views/checkout/CheckoutSuccess.css"></style>
<style scoped>
</style>
